import React from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  Chip,
  useTheme,
  Tooltip,
  IconButton,
} from '@mui/material';
import { Lock as LockIcon, Info as InfoIcon } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSocket } from '../../contexts/SocketContext';
import { useAuth } from '../../contexts/AuthContext';

const Sidebar = ({ menuItems, onItemClick }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { connected } = useSocket();
  const { user } = useAuth();

  const handleItemClick = (path) => {
    navigate(path);
    if (onItemClick) {
      onItemClick();
    }
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Logo Section */}
      <Box
        sx={{
          p: 3,
          backgroundColor: '#ffffff',
          color: '#000000',
          textAlign: 'center',
          borderBottom: '1px solid #e0e0e0',
        }}
      >
        <Box sx={{ mb: 2 }}>
          <img 
            src="/logo.png" 
            alt="Logo" 
            style={{ 
              height: '72px', 
              width: 'auto',
              marginBottom: '8px'
            }} 
          />
        </Box>
        
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            mb: 1,
            color: '#333333',
          }}
        >
          Admin Panel
        </Typography>
        
        {/* Connection Status */}
        <Box sx={{ mt: 1 }}>
          <Chip
            label="Verbunden"
            size="small"
            color="success"
            sx={{
              backgroundColor: '#4CAF50',
              color: 'white',
              fontWeight: 500,
            }}
          />
        </Box>
      </Box>

      <Divider />

      {/* Navigation Menu */}
      <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
        <List sx={{ px: 1, py: 2 }}>
          {menuItems.map((item) => {
            const isActive = location.pathname === item.path;
            const isLocked = item.requiresApproval && (!user || !user.is_approved);
            
            const menuItem = (
              <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
                <ListItemButton
                  onClick={() => !isLocked && handleItemClick(item.path)}
                  sx={{
                    borderRadius: 2,
                    mx: 1,
                    backgroundColor: isActive ? '#FFD700' : 'transparent',
                    color: isActive ? '#000000' : isLocked ? '#666666' : 'inherit',
                    opacity: isLocked ? 0.8 : 1,
                    '&:hover': {
                      backgroundColor: isLocked ? '#f8f8f8' : (isActive ? '#F57F17' : '#f5f5f5'),
                      color: isActive ? '#000000' : isLocked ? '#888888' : 'inherit',
                    },
                    transition: 'all 0.2s ease-in-out',
                    transform: isActive ? 'translateX(4px)' : 'translateX(0)',
                    boxShadow: isActive ? '0 2px 8px rgba(255, 215, 0, 0.3)' : 'none',
                    cursor: isLocked ? 'not-allowed' : 'pointer',
                    border: isLocked ? '1px solid #e0e0e0' : 'none',
                    '&:hover .MuiListItemIcon-root': {
                      color: isLocked ? '#888888' : 'inherit',
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      color: isActive ? '#000000' : isLocked ? '#888888' : 'inherit',
                      minWidth: 40,
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.text}
                    primaryTypographyProps={{
                      fontWeight: isActive ? 600 : 400,
                      fontSize: '0.95rem',
                      color: isLocked ? '#666666' : 'inherit',
                    }}
                  />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {item.description && (
                      <Tooltip
                        title={
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#FFD700' }}>
                              {item.text}
                            </Typography>
                            <Typography variant="body2" sx={{ maxWidth: 250, lineHeight: 1.4 }}>
                              {item.description}
                            </Typography>
                          </Box>
                        }
                        placement="right"
                        arrow
                        componentsProps={{
                          tooltip: {
                            sx: {
                              maxWidth: 300,
                              backgroundColor: 'rgba(0, 0, 0, 0.9)',
                              '& .MuiTooltip-arrow': {
                                color: 'rgba(0, 0, 0, 0.9)',
                              },
                            },
                          },
                        }}
                      >
                        <IconButton
                          size="small"
                          sx={{
                            p: 0.5,
                            color: '#FFD700',
                            '&:hover': {
                              backgroundColor: 'rgba(255, 215, 0, 0.1)',
                              color: '#F57F17',
                            },
                          }}
                        >
                          <InfoIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                    {isLocked && (
                      <LockIcon 
                        sx={{ 
                          fontSize: 16, 
                          color: '#888888',
                        }} 
                      />
                    )}
                  </Box>
                </ListItemButton>
              </ListItem>
            );

            return isLocked ? (
              <Tooltip 
                key={item.text}
                title={
                  <Box>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      Ihr Account muss noch freigeschaltet werden, um auf diesen Bereich zuzugreifen.
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#FFD700' }}>
                      Buchen Sie einen Termin für die Freischaltung:
                    </Typography>
                    <Typography 
                      variant="body2" 
                      component="a" 
                      href="https://meinticketsystem.de/pages/contact" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      sx={{ 
                        color: '#FFD700', 
                        textDecoration: 'underline',
                        '&:hover': { color: '#F57F17' }
                      }}
                    >
                      Termin buchen
                    </Typography>
                  </Box>
                }
                placement="right"
                arrow
                componentsProps={{
                  tooltip: {
                    sx: {
                      maxWidth: 300,
                      backgroundColor: 'rgba(0, 0, 0, 0.9)',
                      '& .MuiTooltip-arrow': {
                        color: 'rgba(0, 0, 0, 0.9)',
                      },
                    },
                  },
                }}
              >
                {menuItem}
              </Tooltip>
            ) : menuItem;
          })}
        </List>
      </Box>

      <Divider />

      {/* Footer */}
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="caption" color="text.secondary">
          Version 1.0.0
        </Typography>
        <br />
        <Typography variant="caption" color="text.secondary">
          © 2025 MTS KiChatbot
        </Typography>
      </Box>
    </Box>
  );
};

export default Sidebar;
